import React from 'react';
import './Otvet.css';

type AnswerMap = Record<string, string>;

type AnswerFileMeta = {
  id: number;
  originalName: string;
  timestamp: string;
  answerCount: number;
};

type DeleteAnswerResponse = {
  success: boolean;
  message?: string;
};

const AUTH_REQUIRED_MESSAGE = 'Требуется авторизация для просмотра ответов';

const triggerBasicAuthPrompt = () => {
  window.location.assign('/otvet');
};

const ensureAuthorizedResponse = (response: Response) => {
  if (response.status === 401) {
    triggerBasicAuthPrompt();
    throw new Error(AUTH_REQUIRED_MESSAGE);
  }

  return response;
};

const parseJsonResponse = async <T,>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) {
    throw new Error('Пустой ответ от сервера');
  }
  if (!contentType.includes('application/json')) {
    throw new Error('Сервер вернул не JSON. Проверьте, что запущен backend (npm start после npm run build).');
  }
  try {
    return JSON.parse(text) as T;
  } catch (error) {
    throw new Error('Некорректный JSON от сервера');
  }
};

const Otvet: React.FC = () => {
  const [entries, setEntries] = React.useState<AnswerMap[]>([]);
  const [metadata, setMetadata] = React.useState<AnswerFileMeta[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [answersResponse, metadataResponse] = await Promise.all([
        fetch('/api/answers').then(ensureAuthorizedResponse),
        fetch('/api/answers/list').then(ensureAuthorizedResponse)
      ]);

      if (!answersResponse.ok) {
        throw new Error('Не удалось загрузить последние ответы');
      }

      if (!metadataResponse.ok) {
        throw new Error('Не удалось загрузить список сохранений');
      }

      const latestAnswers = await parseJsonResponse<AnswerMap>(answersResponse);
      const metadataList = await parseJsonResponse<AnswerFileMeta[]>(metadataResponse);
      const orderedMetadata = [...metadataList].reverse();

      const details = await Promise.all(
        orderedMetadata.map(async (item) => {
          const response = await fetch(`/api/answers/${item.id}`).then(ensureAuthorizedResponse);
          if (!response.ok) {
            return {} as AnswerMap;
          }
          return parseJsonResponse<AnswerMap>(response);
        })
      );

      const allEntries: AnswerMap[] = [];

      if (Object.keys(latestAnswers).length > 0) {
        allEntries.push(latestAnswers);
      }

      allEntries.push(...details);

      setEntries(allEntries);
      setMetadata(orderedMetadata);
      setSelectedIndex((prevIndex) => {
        if (allEntries.length === 0) {
          return 0;
        }
        return Math.min(prevIndex, allEntries.length - 1);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось загрузить данные';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: number, name: string) => {
    const isConfirmed = window.confirm(`Удалить запись "${name}"? Это действие нельзя отменить.`);
    if (!isConfirmed) {
      return;
    }

    setDeletingId(id);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`/api/answers/${id}`, {
        method: 'DELETE'
      }).then(ensureAuthorizedResponse);

      const result = await parseJsonResponse<DeleteAnswerResponse>(response);

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Не удалось удалить запись');
      }

      setSuccessMessage(result.message || 'Запись удалена');
      setSelectedIndex(0);
      await fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось удалить запись';
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const currentEntry = entries[selectedIndex] || {};

  return (
    <div className="background-gradient">
      <section className="section">
        <div className="container otvet-content">
          <div className="otvet-header">
            <h1 className="otvet-title">Результаты опроса</h1>
            <p className="otvet-subtitle">Страница доступна по прямой ссылке. Здесь можно посмотреть ответы участников и удалить ненужные записи.</p>
          </div>

          {loading && <div className="otvet-status">Загрузка данных...</div>}
          {successMessage && <div className="otvet-status otvet-status--success">{successMessage}</div>}
          {error && <div className="otvet-status otvet-status--error">{error}</div>}

          {!loading && !error && entries.length === 0 && (
            <div className="otvet-status">Нет сохраненных ответов.</div>
          )}

          {!loading && !error && entries.length > 0 && (
            <div className="otvet-grid">
              <aside className="otvet-sidebar card">
                <h2 className="otvet-sidebar__title">Сохранения</h2>
                <ul className="otvet-list">
                  <li>
                    <button
                      type="button"
                      className={`otvet-list__item ${selectedIndex === 0 ? 'is-active' : ''}`}
                      onClick={() => setSelectedIndex(0)}
                    >
                      Последние ответы
                    </button>
                  </li>
                  {metadata.map((item, index) => (
                    <li key={item.id} className="otvet-list__row">
                      <button
                        type="button"
                        className={`otvet-list__item ${selectedIndex === index + 1 ? 'is-active' : ''}`}
                        onClick={() => setSelectedIndex(index + 1)}
                      >
                        <span className="otvet-list__title">{item.originalName}</span>
                        <span className="otvet-list__meta">
                          {new Date(item.timestamp).toLocaleString('ru-RU')}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="otvet-list__delete"
                        onClick={() => handleDelete(item.id, item.originalName)}
                        disabled={deletingId === item.id}
                        aria-label={`Удалить ${item.originalName}`}
                        title="Удалить запись"
                      >
                        {deletingId === item.id ? 'Удаление...' : 'Удалить'}
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="otvet-details card">
                <h2 className="otvet-details__title">Ответы</h2>
                <p className="otvet-details__subtitle">Всего ответов: {Object.keys(currentEntry).length}</p>

                <div className="otvet-details__list">
                  {Object.entries(currentEntry).map(([questionId, answer]) => (
                    <div key={questionId} className="otvet-answer">
                      <div className="otvet-answer__id">{questionId}</div>
                      <div className="otvet-answer__text">{answer || '—'}</div>
                    </div>
                  ))}

                  {Object.keys(currentEntry).length === 0 && (
                    <div className="otvet-status">Ответы отсутствуют.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Otvet;
